import React, { forwardRef, useState, useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import styles from './DataTable.module.css';

export interface IDataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  width?: string;
  align?: 'left' | 'right';
  render?: (item: T) => React.ReactNode;
  /** Added to every body cell in the column. */
  className?: string;
  headerClassName?: string;
}

export interface IDataTableProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  columns: IDataTableColumn<T>[];
  data: T[];
  keyField: keyof T;
  onRowClick?: (item: T) => void;
  /** null renders no body at all when there is no data, for a table that is only a header row. */
  emptyMessage?: string | null;
  collapsedRows?: number;
  /**
   * list: fixed column widths, the first column truncates, and space below the table.
   * summary: columns size to their content, cells are taller, and nothing follows the table.
   */
  variant?: 'list' | 'summary';
  dividers?: boolean;
  /** lg rounds the header row with radius-410, md with radius-275. */
  headerRadius?: 'lg' | 'md';
  /** false lets cell content wider than its column show past the table edge. */
  clip?: boolean;
}

function DataTableInner<T>(
  {
    columns,
    data,
    keyField,
    onRowClick,
    emptyMessage = 'No data available',
    collapsedRows,
    variant = 'list',
    dividers = true,
    headerRadius = 'lg',
    clip = true,
    className,
    ...rest
  }: IDataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [expanded, setExpanded] = useState(false);
  // FB-4 (#211): honour prefers-reduced-motion — the repo's first reduced-motion guard.
  // framer-motion's useReducedMotion() short-circuits cleanly; when true we skip the
  // height/opacity transition and reveal rows instantly.
  const reduceMotion = useReducedMotion();

  const visibleData = useMemo(() => {
    if (!collapsedRows || expanded) return data;
    return data.slice(0, collapsedRows);
  }, [data, collapsedRows, expanded]);

  const showExpand = collapsedRows && data.length > collapsedRows;

  const cellClassName = (col: IDataTableColumn<T>, own?: string) =>
    [col.align === 'right' && styles.rightAlign, own].filter(Boolean).join(' ') || undefined;

  const renderCells = (item: T) =>
    columns.map((col) => (
      <td key={col.key} className={cellClassName(col, col.className)}>
        {col.render ? col.render(item) : String((item as Record<string, unknown>)[col.key] ?? '')}
      </td>
    ));

  const tableClassName = [
    styles.table,
    styles[variant],
    !dividers && styles.noDividers,
    headerRadius === 'md' && styles.mdHeader,
  ]
    .filter(Boolean)
    .join(' ');

  const rowClassName = onRowClick ? styles.clickableRow : undefined;
  const onRowClickHandler = onRowClick ? (item: T) => () => onRowClick(item) : undefined;

  // Rows beyond the collapsed threshold animate in on expand (height + opacity). Rows still
  // mount/unmount to the same final counts the existing collapsedRows suite asserts: when
  // collapsed only the first `collapsedRows` are rendered, expanded renders all of them — the
  // animation only governs how the extra rows appear, not whether they're in the DOM.
  // table-layout: fixed fights animating <tr> height directly, so we animate maxHeight (capped
  // generously) alongside opacity to avoid column-layout thrash.
  const collapsedCount = collapsedRows ?? 0;

  return (
    <div ref={ref} className={className} {...rest}>
      <div className={[styles.tableWrapper, clip && styles.clip].filter(Boolean).join(' ')}>
        <table className={tableClassName}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={cellClassName(col, col.headerClassName)}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          {(visibleData.length > 0 || emptyMessage !== null) && (
            <tbody>
              {visibleData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>
                    <div className={styles.emptyState}>
                      <p>{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                visibleData.map((item, index) => {
                  const key = String(item[keyField]);
                  // Always-visible rows render as plain <tr> (unchanged behaviour). Rows revealed
                  // by "View More" render as motion.tr with the animatedRow marker.
                  const isExtraRow = !!collapsedRows && index >= collapsedCount;
                  if (!isExtraRow) {
                    return (
                      <tr key={key} className={rowClassName} onClick={onRowClickHandler?.(item)}>
                        {renderCells(item)}
                      </tr>
                    );
                  }
                  return (
                    <motion.tr
                      key={key}
                      className={[styles.animatedRow, rowClassName].filter(Boolean).join(' ')}
                      onClick={onRowClickHandler?.(item)}
                      initial={reduceMotion ? false : { opacity: 0, maxHeight: 0 }}
                      animate={{ opacity: 1, maxHeight: 200 }}
                      transition={
                        reduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }
                      }
                    >
                      {renderCells(item)}
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          )}
        </table>
        {showExpand && !expanded && <div className={styles.tableFade} />}
      </div>
      {showExpand && (
        <button className={styles.expandBtn} onClick={() => setExpanded(!expanded)}>
          {expanded ? 'View Less' : 'View More'}
        </button>
      )}
    </div>
  );
}

const DataTableWithRef = forwardRef(DataTableInner);
DataTableWithRef.displayName = 'DataTable';

// forwardRef erases the row type parameter; the cast restores it for callers.
const DataTable = DataTableWithRef as unknown as <T>(
  props: IDataTableProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

export default DataTable;
